import { useRef, useState, useCallback } from "react";
import {
  Box,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { DeleteForever, RestoreFromTrash } from "@mui/icons-material";
import { visuallyHidden } from "@mui/utils";
import {
  loadChannels,
  deleteChannel,
  restoreChannel,
} from "../../pages/app/utils/dataLoader";
import { useEditorPreferences } from "../../store/editor/hooks";
import { useChannels } from "../../store/channels/hooks";

const ChannelSelector = ({ clip, preferencesSave }) => {
  const theme = useTheme();
  const selectWidth = 240;
  const selectRef = useRef(null);
  const [open, setOpen] = useState(false);
  const { preferences, updateSelectedChannel } = useEditorPreferences();
  const { selectedChannel } = preferences;
  const { channelList, setChannelList } = useChannels();

  const handleClose = () => {
    setOpen(false);
    selectRef?.current?.classList.remove("Mui-focused");
  };

  const handleOpen = () => {
    setOpen(true);
    selectRef?.current?.classList.add("Mui-focused");
  };

  const handleToggle = (e, channel) => {
    e.stopPropagation();
    if (channel.status === "ACTIVE") {
      handleDeleteChannel(channel.id);
    } else {
      handleRestoreChannel(channel.id);
    }
  };

  const setChannel = (channel) => {
    console.log("setting channel", channel);
    updateSelectedChannel(channel);
    preferencesSave({ selectedChannel: channel });
  };

  const loadChannelData = useCallback(async () => {
    const channels = await loadChannels(clip.caseId, clip.id);
    setChannelList(channels);
  }, [clip.caseId, clip.id, setChannelList]);

  const handleDeleteChannel = async (id) => {
    console.log("Deleting channel with ID:", id);
    await deleteChannel(clip.caseId, clip.id, id);
    await loadChannelData();
  };

  const handleRestoreChannel = async (id) => {
    console.log("Restoring channel with ID:", id);
    await restoreChannel(clip.caseId, clip.id, id);
    await loadChannelData();
  };

  const handleChange = (e) => {
    const selected = channelList[e.target.value - 1];
    if (selected.status === "ACTIVE") setChannel(e.target.value);
  };

  return (
    <FormControl
      size="small"
      sx={{
        background: theme.palette.background.paper,
        borderRadius: 1,
        boxShadow: 1,
        left: 58,
        position: "absolute",
        top: 8,
        zIndex: 10,
      }}
    >
      <InputLabel id="channels-select" sx={{ display: visuallyHidden }}>
        Channel Select
      </InputLabel>
      {Boolean(channelList.length) ? (
        <Select
          ref={selectRef}
          displayEmpty
          id="channels-select"
          inputProps={{ "aria-label": "channels-select" }}
          MenuProps={{ disableRestoreFocus: true, "aria-hidden": !open }}
          onChange={handleChange}
          onClose={handleClose}
          onOpen={handleOpen}
          open={open}
          value={channelList.length > 0 ? selectedChannel : ""}
          sx={{
            width: selectWidth,
            "& .MuiSelect-select .MuiIconButton-root": { display: "none" },
          }}
        >
          {channelList?.map((chan) => (
            <MenuItem
              key={chan.id}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                width: selectWidth,
              }}
              value={channelList.indexOf(chan) + 1}
            >
              <Typography
                onClick={(e) => chan.status !== "ACTIVE" && e.stopPropagation()}
                sx={{
                  opacity: chan.status !== "ACTIVE" ? 0.5 : 1,
                  textDecoration: chan.status === "DELETED" && "line-through",
                  width: 1,
                }}
              >
                Channel {channelList.indexOf(chan) + 1} (of {channelList.length}
                )
              </Typography>
              {chan.status !== "DELETED" ? (
                <Tooltip
                  title={
                    chan.status === "ACTIVE"
                      ? "Delete Channel"
                      : "Restore Channel"
                  }
                >
                  <IconButton
                    size="small"
                    onClick={(e) => handleToggle(e, chan)}
                  >
                    {chan.status === "ACTIVE" ? (
                      <DeleteForever color="error" fontSize="small" />
                    ) : (
                      <RestoreFromTrash color="secondary" fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              ) : null}
            </MenuItem>
          ))}
        </Select>
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: 40,
            width: selectWidth,
          }}
        >
          <CircularProgress size={20} />
        </Box>
      )}
    </FormControl>
  );
};

export default ChannelSelector;
